import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import { scrollToSection } from "@/lib/scroll";
import { Logo } from "@/components/Logo";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (l) => {
    const wasOpen = open;
    setOpen(false);
    const run = () => {
      if (l.type === "route") {
        navigate(l.to);
      } else if (location.pathname !== "/") {
        navigate("/", { state: { scrollTo: l.id } });
      } else {
        scrollToSection(l.id);
      }
    };
    setTimeout(run, wasOpen ? 250 : 0);
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/");
    else scrollToSection("home");
  };

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color,padding] duration-500 ${
          scrolled ? "border-b border-slate-200/80 bg-white/85 backdrop-blur-xl" : "bg-transparent"
        }`}
      >
        <div className={`mx-auto flex max-w-7xl items-center justify-between px-5 transition-all duration-500 sm:px-8 ${scrolled ? "py-3" : "py-5"}`}>
          <button type="button" onClick={goHome} aria-label="Starting Point Agency — home" data-testid="nav-logo-button" className="shrink-0">
            <Logo compact={scrolled} />
          </button>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Navigazione principale">
            {NAV_LINKS.slice(1).map((l) => (
              <button
                key={l.id}
                type="button"
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l)}
                className="font-display text-[12px] font-bold tracking-[0.14em] text-navy/60 transition-colors duration-300 hover:text-brand-blue"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="nav-cta"
              onClick={() => go({ id: "contatti", type: "anchor" })}
              className="group hidden items-center gap-2 rounded-full bg-brand-gradient px-5 py-2.5 font-display text-[12px] font-bold tracking-[0.12em] text-white shadow-[0_10px_28px_-10px_rgba(37,99,235,0.55)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_34px_-8px_rgba(56,189,248,0.5)] sm:inline-flex"
            >
              INIZIA ORA
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M7 17 17 7" /><path d="M8 7h9v9" />
              </svg>
            </button>
            <button
              type="button"
              data-testid="nav-menu-button"
              onClick={() => setOpen(true)}
              aria-label="Apri menu"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-navy/15 text-navy lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] flex flex-col bg-navy bg-grid-dark"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-between px-5 py-5">
              <Logo dark />
              <button type="button" data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Chiudi menu" className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 px-8" aria-label="Menu mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  type="button"
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => go(l)}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="group flex items-baseline gap-4 py-2 text-left"
                >
                  <span className="font-mono text-xs text-brand-cyan">0{i + 1}</span>
                  <span className="font-display text-4xl font-black tracking-tight text-white transition-colors group-hover:text-brand-cyan sm:text-5xl">
                    {l.label}
                  </span>
                </motion.button>
              ))}
            </nav>
            <div className="px-8 pb-10">
              <p className="font-display text-[11px] font-bold uppercase tracking-[0.3em] text-white/40">Where visibility starts.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
